/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';

interface BrushSelectorProps {
  color: string;
  setColor: (color: string) => void;
  lineWidth: number;
  setLineWidth: (width: number) => void;
  brushColors: string[];
  isActive: boolean;
}

export const BrushSelector: React.FC<BrushSelectorProps> = ({
  color,
  setColor,
  lineWidth,
  setLineWidth,
  brushColors,
  isActive,
}) => {
  if (!isActive) return null;

  return (
    <div className="flex flex-col gap-3 p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800" id="brush-selector-tool">
      {/* Stroke options */}
      <div className="flex items-center justify-between gap-2.5">
        <span className="text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider">Stroke Width:</span>
        <div className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-950 p-1 rounded-lg border border-slate-200 dark:border-slate-800">
          {[2, 4, 8, 12].map((size) => (
            <button
              key={size}
              type="button"
              onClick={() => setLineWidth(size)}
              className={`w-7 h-7 rounded-lg flex items-center justify-center border transition-all ${
                lineWidth === size
                  ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 font-bold text-xs'
                  : 'border-transparent hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 text-xs'
              }`}
              title={`${size}px brush`}
            >
              <div
                className="bg-slate-800 dark:bg-slate-200 rounded-full"
                style={{ width: `${Math.min(size + 1, 14)}px`, height: `${Math.min(size + 1, 14)}px` }}
              />
            </button>
          ))}
        </div>
      </div>

      {/* Colors palette */}
      <div className="flex flex-col gap-1.5 border-t border-slate-100 dark:border-slate-800 pt-2.5">
        <span className="text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider">Color Palette:</span>
        <div className="flex items-center gap-2 flex-wrap">
          {brushColors.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              className={`w-6 h-6 rounded-full shrink-0 border-2 transition-transform ${
                color === c
                  ? 'scale-115 border-indigo-600 ring-2 ring-indigo-100 dark:ring-indigo-950/60'
                  : 'border-transparent hover:scale-105'
              }`}
              style={{ backgroundColor: c }}
              title={c}
            />
          ))}
          <div className="ml-auto flex items-center">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-6 h-6 cursor-pointer rounded-full p-0 border-0 outline-none"
              title="Custom Color Pick"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
